import { AlertTriangle, Briefcase, Check, FileCheck, Mic, RotateCcw, X } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { useEffect, useRef, useState, type ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

/**
 * A scripted walk through the three jobs PRAXA does, for people who have not
 * signed in yet.
 *
 * Nothing here calls a model or the API: a visitor has no account, so no
 * allowance, and a landing page that spends money on every view is a landing
 * page someone will reload in a loop. The candidate, the company and the
 * posting are invented, and the panel says so. The shapes are the real ones -
 * fit is 0-1 with matched / missing requirements, the reviewer flags claims the
 * CV does not support, and an interview answer gets five rubric scores out of 5 -
 * so what people see here is what they get after signing in.
 */

type DemoKey = "find" | "write" | "practise"

const TABS: { key: DemoKey; label: string; short: string; icon: LucideIcon }[] = [
  { key: "find", label: "Find a job", short: "Find", icon: Briefcase },
  { key: "write", label: "Write the CV", short: "Write", icon: FileCheck },
  { key: "practise", label: "Practise", short: "Practise", icon: Mic },
]

/** How many beats each script has; one beat is revealed every `BEAT_MS`. */
const BEATS: Record<DemoKey, number> = { find: 7, write: 6, practise: 8 }
const BEAT_MS = 900

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/**
 * The beat a script has reached. Starts when the panel scrolls into view, so
 * the animation is not over before anyone looks at it. With reduced motion the
 * whole script is shown at once.
 */
function useBeats(total: number) {
  const ref = useRef<HTMLDivElement>(null)
  const [beat, setBeat] = useState(() => (prefersReducedMotion() ? total : 0))
  const done = beat >= total

  // Runs once per mount, and once more when the script finishes (to stop
  // watching). "Replay" remounts the panel rather than resetting state here.
  useEffect(() => {
    const node = ref.current
    if (!node || done) return
    let timer: number | undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || timer !== undefined) return
        timer = window.setInterval(() => {
          setBeat((current) => {
            if (current + 1 >= total && timer !== undefined) window.clearInterval(timer)
            return Math.min(current + 1, total)
          })
        }, BEAT_MS)
      },
      { threshold: 0.35 },
    )
    observer.observe(node)
    return () => {
      observer.disconnect()
      if (timer !== undefined) window.clearInterval(timer)
    }
  }, [total, done])

  return { ref, beat }
}

export function Demo() {
  const [tab, setTab] = useState<DemoKey>("find")
  const [run, setRun] = useState(0)

  return (
    <div className="overflow-hidden rounded-2xl border bg-card/70 shadow-xl shadow-black/5 backdrop-blur">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex gap-1 rounded-lg bg-muted p-1" role="tablist" aria-label="Demo">
          {TABS.map(({ key, label, short, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              aria-label={label}
              onClick={() => {
                setTab(key)
                setRun((value) => value + 1)
              }}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-colors sm:text-sm",
                tab === key ? "bg-background font-medium shadow-sm" : "text-muted-foreground",
              )}
            >
              <Icon className="size-3.5" aria-hidden />
              <span className="sm:hidden">{short}</span>
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Example - invented candidate
          </span>
          <Button variant="ghost" size="sm" onClick={() => setRun((value) => value + 1)}>
            <RotateCcw aria-hidden />
            Replay
          </Button>
        </div>
      </div>

      <div className="min-h-[25rem] p-5 sm:p-6">
        {tab === "find" ? <FindDemo key={run} /> : null}
        {tab === "write" ? <WriteDemo key={run} /> : null}
        {tab === "practise" ? <PractiseDemo key={run} /> : null}
      </div>
    </div>
  )
}

/** Fades a block in once the script reaches its beat. */
function Reveal({ at, beat, children, className }: { at: number; beat: number; children: ReactNode; className?: string }) {
  const shown = beat >= at
  return (
    <div
      aria-hidden={!shown}
      className={cn(
        "transition-all duration-500",
        shown ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-1 opacity-0",
        className,
      )}
    >
      {children}
    </div>
  )
}

function Bar({ value, shown, tone = "primary" }: { value: number; shown: boolean; tone?: "primary" | "success" | "warning" }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={cn(
          "h-full rounded-full transition-[width] duration-1000 ease-out",
          tone === "success" ? "bg-success" : tone === "warning" ? "bg-warning" : "bg-primary",
        )}
        style={{ width: shown ? `${value * 100}%` : "0%" }}
      />
    </div>
  )
}

const REQUIREMENTS: { need: string; evidence: string | null }[] = [
  { need: "ISO/SAE 21434 threat analysis (TARA)", evidence: "Led the TARA for three ECU programmes" },
  { need: "Penetration testing over CAN", evidence: "Grey-box tests on gateway ECUs with CANoe" },
  { need: "CI/CD and Docker", evidence: "Built the GitLab pipeline for the test bench" },
  { need: "AUTOSAR SecOC", evidence: null },
]

function FindDemo() {
  const { ref, beat } = useBeats(BEATS.find)
  return (
    <div ref={ref} className="grid gap-5 lg:grid-cols-[1fr_1.2fr]">
      <div>
        <div className="rounded-xl border bg-background/60 p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Posting - from a public job board
          </p>
          <p className="mt-2 font-medium">Embedded Security Engineer</p>
          <p className="text-sm text-muted-foreground">Nordwerk Mobility GmbH - Stuttgart - hybrid</p>
          <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
            {REQUIREMENTS.map((item) => (
              <li key={item.need}>- {item.need}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="space-y-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-primary">
          Checked against your CV
        </p>
        {REQUIREMENTS.map((item, index) => (
          <Reveal key={item.need} at={index + 1} beat={beat}>
            <div className="flex gap-3 rounded-lg border bg-background/60 p-3 text-sm">
              {item.evidence ? (
                <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
              ) : (
                <X className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
              )}
              <div>
                <p className="font-medium">{item.need}</p>
                <p className="text-xs text-muted-foreground">
                  {item.evidence ? `CV: "${item.evidence}"` : "Not in your CV - listed as a gap"}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
        <Reveal at={5} beat={beat}>
          <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-medium">Fit</span>
              <span className="font-mono">0.78 / 1</span>
            </div>
            <Bar value={0.78} shown={beat >= 5} />
          </div>
        </Reveal>
        <Reveal at={6} beat={beat}>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Apply, with one caveat:</span> three of
            four requirements are in your CV. Say in the letter how you would pick up SecOC.
          </p>
        </Reveal>
      </div>
    </div>
  )
}

function WriteDemo() {
  const { ref, beat } = useBeats(BEATS.write)
  return (
    <div ref={ref} className="grid gap-5 lg:grid-cols-2">
      <div className="space-y-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Your CV says
        </p>
        <div className="rounded-xl border bg-background/60 p-4 text-sm leading-relaxed">
          Responsible for security testing of vehicle ECUs. Worked with CANoe. Wrote test reports
          for customers.
        </div>
        <Reveal at={1} beat={beat}>
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary">
            Writing agent - tailored for the posting
          </p>
          <div className="mt-2 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm leading-relaxed">
            Ran grey-box penetration tests on vehicle ECUs over CAN with CANoe, and reported the
            findings to customers.{" "}
            Reduced open findings by{" "}
            <span
              className={cn(
                "rounded border border-dashed border-warning/60 px-1 font-mono text-xs transition-colors duration-500",
                beat >= 3 ? "bg-warning/25" : "",
              )}
            >
              [your number]
            </span>
            .
          </div>
        </Reveal>
      </div>

      <div className="space-y-3">
        <Reveal at={2} beat={beat}>
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary">
            Reviewing agent - checks every claim against your CV
          </p>
        </Reveal>
        <Reveal at={2} beat={beat}>
          <div className="flex gap-3 rounded-lg border bg-background/60 p-3 text-sm">
            <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
            <span>"Grey-box penetration tests ... over CAN" - supported by your CV</span>
          </div>
        </Reveal>
        <Reveal at={3} beat={beat}>
          <div className="flex gap-3 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
            <span>
              "Reduced open findings by ..." - <span className="font-medium">your CV gives no
              figure, so none was made up.</span> Add the real number, or drop the line.
            </span>
          </div>
        </Reveal>
        <Reveal at={4} beat={beat}>
          <div className="rounded-lg border bg-background/60 p-3 text-sm">
            <p className="font-medium">1 gap for you to fill</p>
            <p className="text-xs text-muted-foreground">
              Nothing is saved yet. You read it, then decide.
            </p>
          </div>
        </Reveal>
        <Reveal at={5} beat={beat} className="flex gap-2">
          <Button size="sm" tabIndex={-1}>
            Approve
          </Button>
          <Button size="sm" variant="outline" tabIndex={-1}>
            Discard
          </Button>
        </Reveal>
      </div>
    </div>
  )
}

const RUBRIC: { label: string; score: number }[] = [
  { label: "Structure", score: 4 },
  { label: "Specificity", score: 3 },
  { label: "Ownership", score: 5 },
  { label: "Impact", score: 2 },
  { label: "Relevance", score: 4 },
]

function PractiseDemo() {
  const { ref, beat } = useBeats(BEATS.practise)
  return (
    <div ref={ref} className="grid gap-5 lg:grid-cols-2">
      <div className="space-y-3">
        <div className="max-w-[90%] rounded-2xl rounded-tl-sm border bg-background/60 p-3 text-sm">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Interviewer
          </p>
          Tell me about a time you found a security problem late in a project.
        </div>
        <Reveal at={1} beat={beat} className="flex justify-end">
          <div className="max-w-[90%] rounded-2xl rounded-tr-sm bg-primary p-3 text-sm text-primary-foreground">
            Two weeks before release I found the diagnostic port accepted unsigned firmware. I
            wrote the attack path, showed it to the ECU team and we agreed a signed-update check
            for the release.
          </div>
        </Reveal>
      </div>

      <div className="space-y-3">
        <Reveal at={2} beat={beat}>
          <p className="font-mono text-[10px] uppercase tracking-widest text-primary">
            Scored on five things, each out of 5
          </p>
        </Reveal>
        {RUBRIC.map((item, index) => (
          <Reveal key={item.label} at={index + 2} beat={beat}>
            <div className="flex items-center gap-3 text-sm">
              <span className="w-24 shrink-0">{item.label}</span>
              <Bar
                value={item.score / 5}
                shown={beat >= index + 2}
                tone={item.score <= 2 ? "warning" : item.score >= 4 ? "success" : "primary"}
              />
              <span className="w-8 shrink-0 text-right font-mono text-xs">{item.score}/5</span>
            </div>
          </Reveal>
        ))}
        <Reveal at={7} beat={beat}>
          <p className="rounded-lg border bg-background/60 p-3 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Next time:</span> you said what you did
            but not what changed. Was the release on time? How many vehicles got the fix?
          </p>
        </Reveal>
      </div>
    </div>
  )
}
